import React from 'react';

import type { VERSION } from '../typings/react';

export function makeEnum<X extends string | number>(
    ...x: X[]
): { [K in X]: K } {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    const o: { [K in X]: K } = {} as any;

    for (const item of x) {
        o[item] = item;
    }

    return o;
}

export type enumType<T> = T[keyof T];

export type Extends<T, U extends T> = U;

export interface BaseProps {
    className?: string;
    'data-role'?: string;
    'data-role-value'?: string;
}

export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

function isObject(obj: unknown): obj is Record<string, unknown> {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        Object.getPrototypeOf(obj) === Object.prototype
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deepMerge<T extends Record<string, any>>(
    ...objects: DeepPartial<T>[]
): T {
    if (objects.length === 0) {
        throw new Error('deepMerge requires at least one argument');
    }

    const target = objects[0] as T;

    for (let i = 1; i < objects.length; i++) {
        const source = objects[i];

        for (const key in source) {
            if (Object.prototype.hasOwnProperty.call(source, key)) {
                if (isObject(source[key])) {
                    if (!isObject(target[key])) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                        target[key] = {} as any;
                    }
                    // @ts-ignore
                    deepMerge(target[key], source[key]);
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                    target[key] = source[key] as any;
                }
            }
        }
    }

    return target;
}

export function toArray<T>(
    arrayOrElement: NonNullable<T>[] | NonNullable<T>
): T[];
export function toArray<T>(
    arrayOrElement: NonNullable<T>[] | NonNullable<T> | undefined
): T[] | undefined;
export function toArray<T>(
    arrayOrElement: NonNullable<T>[] | NonNullable<T> | undefined
): NonNullable<T>[] | undefined {
    if (arrayOrElement === null || arrayOrElement === undefined) {
        return undefined;
    }
    if (Array.isArray(arrayOrElement)) {
        return arrayOrElement;
    }
    return [arrayOrElement];
}

export function flattenChildren<T extends React.ReactNode>(children: T | T[]) {
    const innerChildren = React.Children.toArray(children) as T[];

    const flatten = (children: T[]): T[] => {
        return React.Children.toArray(children).flatMap(stripFragment) as T[];
    };

    return flatten(flatten(innerChildren)).filter(Boolean) as Exclude<
        T,
        null | undefined | false
    >[];
}

function stripFragment<T>(child: T): T {
    if (
        child &&
        React.isValidElement<{ children?: unknown }>(child) &&
        child.type === React.Fragment
    ) {
        return child.props.children as T;
    }

    return child;
}

/**
 * Adds a VERSION property to a React component for Figma design sync tracking.
 *
 * @param component - The React component to version
 * @param version - Version string in format "1.0.0"
 * @returns The component with VERSION property attached
 *
 * @example
 * const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(...);
 * export const Button = withVersion(ButtonComponent, '1.0.0');
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withVersion<T extends React.ComponentType<any>>(
    component: T,
    version: VERSION
): T & { VERSION: VERSION; displayName?: string } {
    return Object.assign(component, { VERSION: version });
}

export function reactElementSerializer(key: string, value: unknown) {
    if (typeof value === 'function') {
        return `[Function: ${value.name ?? 'anonymous'}]`;
    }

    if (React.isValidElement(value) && key) {
        const displayName =
            typeof value.type === 'string'
                ? value.type
                : (value.type as React.FunctionComponent).displayName ||
                  value.type.name ||
                  'Component';

        return `<${displayName}>${JSON.stringify(value.props, reactElementSerializer)}</${displayName}>`;
    }

    return value;
}
